/**
 * Sync Service
 * Handles data synchronization between local storage and server
 * Supports periodic background sync and on-demand sync
 */

import { PersonRecord, UserAppData } from '../types/data';
import { storageService } from './storage.service';
import { authService } from './auth.service';
import { logService } from './log.service';
import { API_CONFIG, SYNC_CONFIG } from '../config/app.config';
import axios from 'axios';

export interface SyncResult {
  success: boolean;
  uploaded: number;
  downloaded: number;
  conflicts: number;
  error?: string;
}

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncAt?: string;
  lastSyncResult?: SyncResult;
  pendingChanges: number;
  progress?: number; // 0..100
  stage?: 'idle' | 'preparing' | 'uploading' | 'downloading' | 'merging' | 'reconciling' | 'done' | 'error';
}

class SyncService {
  private syncInterval: NodeJS.Timeout | null = null;
  private isSyncing = false;
  private syncListeners: Array<(status: SyncStatus) => void> = [];
  private lastSyncResult?: SyncResult;
  private progress = 0;
  private stage: SyncStatus['stage'] = 'idle';

  private apiClient = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  constructor() {
    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      async (config) => {
        const token = await storageService.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Initialize sync service
   * Sets up periodic sync if enabled
   */
  async initialize(): Promise<void> {
    const settings = await storageService.getSettings();
    
    if (settings.autoSyncEnabled) {
      this.startPeriodicSync(settings.syncInterval);
    }

    // Sync on app start if enabled
    if (SYNC_CONFIG.AUTO_SYNC_ON_START) {
      const isAuthenticated = await authService.isAuthenticated();
      if (isAuthenticated) {
        this.syncNow().catch(console.error);
      }
    }
  }

  /**
   * Start periodic background sync
   */
  startPeriodicSync(intervalMinutes: number): void {
    this.stopPeriodicSync();

    const intervalMs = intervalMinutes * 60 * 1000;
    this.syncInterval = setInterval(async () => {
      const settings = await storageService.getSettings();
      const isAuthenticated = await authService.isAuthenticated();

      // Check if we should sync
      if (!isAuthenticated || settings.offlineMode) {
        return;
      }

      // Check WiFi only setting
      if (settings.syncOnWifiOnly && !this.isOnWiFi()) {
        console.log('Sync skipped: WiFi only mode enabled and not on WiFi');
        return;
      }

      await this.syncNow();
    }, intervalMs);
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Perform immediate sync
   */
  async syncNow(): Promise<SyncResult> {
    if (this.isSyncing) {
      return {
        success: false,
        uploaded: 0,
        downloaded: 0,
        conflicts: 0,
        error: 'Sync already in progress',
      };
    }

    this.isSyncing = true;
    this.progress = 0;
    this.stage = 'uploading';
    await this.notifyListeners();

    const result: SyncResult = {
      success: false,
      uploaded: 0,
      downloaded: 0,
      conflicts: 0,
    };

    try {
      const isAuthenticated = await authService.isAuthenticated();
      if (!isAuthenticated) {
        throw new Error('Not authenticated');
      }

      const token = await storageService.getAuthToken();
      if (!token) {
        throw new Error('No auth token');
      }

      // Configure API client with auth header
      this.apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Get local data
      const userData = await storageService.getUserData();
      if (!userData) {
        throw new Error('No local data to sync');
      }

      // Step 1: Upload local changes
      const uploadResult = await this.uploadData(userData);
      result.uploaded = uploadResult.uploaded;
      result.conflicts += uploadResult.conflicts;

      this.progress = 40;
      this.stage = 'downloading';
      await this.notifyListeners();

      // Step 2: Download server data
      const downloadResult = await this.downloadData();
      result.downloaded = downloadResult.downloaded;
      result.conflicts += downloadResult.conflicts;

      this.progress = 75;
      this.stage = 'merging';
      await this.notifyListeners();

      // Step 3: Merge and save - this must complete successfully before updating sync time
      await this.mergeAndSave(userData, downloadResult.data, downloadResult.deletedIds);

      // Only update sync timestamp AFTER successful merge and save
      // This prevents data loss if merge/save fails - next sync will retry from last successful point
      const nextSyncTime = downloadResult.serverTimestamp ?? new Date().toISOString();
      await storageService.setLastSyncTime(nextSyncTime);

      this.progress = 100;
      
      result.success = true;
      result.error = undefined;
      this.stage = 'done';
    } catch (error: any) {
      console.error('Sync error:', error);
      result.success = false;
      result.error = error.message || 'Sync failed';
      this.stage = 'error';
    } finally {
      this.isSyncing = false;
      this.lastSyncResult = result;
      await this.notifyListeners();
    }

    return result;
  }

  /**
   * Reconciliation sync - compares local data with server and syncs any differences
   * This is more robust than timestamp-based sync and catches any missed records
   */
  async syncWithReconciliation(): Promise<SyncResult> {
    if (this.isSyncing) {
      return this.lastSyncResult || {
        success: false,
        uploaded: 0,
        downloaded: 0,
        conflicts: 0,
        error: 'Sync already in progress',
      };
    }

    this.isSyncing = true;
    this.progress = 0;
    this.stage = 'preparing';

    const result: SyncResult = {
      success: false,
      uploaded: 0,
      downloaded: 0,
      conflicts: 0,
    };

    try {
      await this.notifyListeners();

      const token = await storageService.getAuthToken();
      if (!token) {
        throw new Error('No auth token');
      }

      this.apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const userData = await storageService.getUserData();
      if (!userData) {
        throw new Error('No local data');
      }

      this.progress = 10;
      this.stage = 'uploading';
      await this.notifyListeners();

      // Step 1: Upload any dirty records first (same as regular sync)
      const uploadResult = await this.uploadData(userData);
      result.uploaded = uploadResult.uploaded;
      result.conflicts += uploadResult.conflicts;

      this.progress = 30;
      this.stage = 'reconciling';
      await this.notifyListeners();

      // Step 2: Build and send manifest to server
      const persons: PersonRecord[] = Array.isArray((userData as any).persons) ? (userData as any).persons : [];
      const manifest = persons.map(p => ({
        id: p.id,
        updated_at: p.updated_at || '1970-01-01T00:00:00.000Z'
      }));

      const response = await this.apiClient.post(API_CONFIG.ENDPOINTS.SYNC_RECONCILE, {
        manifest
      });

      await logService.info('Sync Reconcile', 'Reconciliation response', response.data.summary);

      // Step 3: Upload records that are missing on server
      const missingOnServer: string[] = response.data.missing_on_server || [];
      if (missingOnServer.length > 0) {
        this.progress = 50;
        this.stage = 'uploading';
        await this.notifyListeners();

        // Find the person records that need to be uploaded
        const personsToUpload = persons.filter(p => missingOnServer.includes(p.id));
        
        if (personsToUpload.length > 0) {
          await logService.info('Sync Reconcile', 'Uploading missing records to server', {
            count: personsToUpload.length,
            ids: personsToUpload.map(p => p.id)
          });

          const pushResponse = await this.apiClient.post(API_CONFIG.ENDPOINTS.SYNC_UPLOAD, {
            persons: personsToUpload
          });

          result.uploaded += pushResponse.data.synced_count || 0;
          result.conflicts += (pushResponse.data.conflicts?.length || 0);
        }
      }

      this.progress = 60;
      this.stage = 'downloading';
      await this.notifyListeners();

      // Step 4: Merge missing and outdated records from server
      const missingRecords: PersonRecord[] = response.data.missing_on_client || [];
      const outdatedRecords: PersonRecord[] = response.data.outdated_on_client || [];
      const allServerRecords = [...missingRecords, ...outdatedRecords];
      const deletedIds: string[] = response.data.deleted_ids || [];

      result.downloaded = allServerRecords.length;

      this.progress = 75;
      this.stage = 'merging';
      await this.notifyListeners();

      // Step 5: Merge and save
      await this.mergeAndSave(userData, allServerRecords, deletedIds);

      // Step 6: Update sync timestamp
      const serverTimestamp = response.data.server_timestamp ?? new Date().toISOString();
      await storageService.setLastSyncTime(serverTimestamp);

      this.progress = 100;
      result.success = true;
      result.error = undefined;
      this.stage = 'done';

      await logService.info('Sync Reconcile', 'Reconciliation complete', {
        uploaded: result.uploaded,
        downloaded: result.downloaded,
        missing: missingRecords.length,
        outdated: outdatedRecords.length
      });

    } catch (error: any) {
      console.error('Reconciliation sync error:', error);
      result.success = false;
      result.error = error.message || 'Reconciliation sync failed';
      this.stage = 'error';
    } finally {
      this.isSyncing = false;
      this.lastSyncResult = result;
      await this.notifyListeners();
    }

    return result;
  }

  /**
   * Upload local data to server
   */
  private async uploadData(userData: UserAppData): Promise<{ uploaded: number; conflicts: number }> {
    const persons: PersonRecord[] = Array.isArray((userData as any).persons) ? (userData as any).persons : [];
    if (!Array.isArray((userData as any).persons)) {
      (userData as any).persons = persons;
    }

    const deletedPersons: Array<{ id: string; deleted_at: string }> = Array.isArray((userData as any).deleted_persons)
      ? (userData as any).deleted_persons
      : [];
    if (!Array.isArray((userData as any).deleted_persons)) {
      (userData as any).deleted_persons = deletedPersons;
    }

    // Get current user's UUID for filtering
    const currentUser = await authService.getCurrentUser();
    const userAny = currentUser as any;
    const surveyorUuid: string | undefined = (typeof userAny?.uuid === 'string' && userAny.uuid.length > 0)
      ? userAny.uuid
      : (currentUser?.id ? String(currentUser.id) : undefined);

    const isDirty = (person: PersonRecord): boolean => {
      if (!person.synced_at) return true;
      if (!person.updated_at || String(person.updated_at).trim().length === 0) return true;
      const updated = new Date(person.updated_at || 0).getTime();
      const synced = new Date(person.synced_at || 0).getTime();
      return updated > synced;
    };

    // Only upload persons that belong to the current user (surveyor_id matches)
    // This prevents offline user records from being pushed to server
    const personsToUpload: PersonRecord[] = persons.filter((p) => {
      if (!isDirty(p)) return false;
      // Only include if surveyor_id matches current user's uuid
      if (surveyorUuid && p.surveyor_id !== surveyorUuid) return false;
      return true;
    });

    await logService.info('Sync Upload', 'Filtered persons for upload', {
      totalPersons: persons.length,
      dirtyPersons: personsToUpload.length,
      currentUserUuid: surveyorUuid,
      personsSummary: personsToUpload.map(p => ({
        id: p.id,
        name: p.name,
        surveyor_id: p.surveyor_id,
        recordCount: p.records?.length || 0,
        updated_at: p.updated_at,
        synced_at: p.synced_at,
      })),
    });
    
    const deletedIdsToUpload: string[] = deletedPersons.map((d) => d.id).filter((id) => typeof id === 'string' && id.length > 0);

    if (personsToUpload.length === 0 && deletedIdsToUpload.length === 0) {
      return { uploaded: 0, conflicts: 0 };
    }

    try {

      // Server requires persons.*.updated_at; ensure every uploaded person has it.
      const nowIso = new Date().toISOString();
      for (const person of personsToUpload) {
        if (!person.updated_at || String(person.updated_at).trim().length === 0) {
          person.updated_at = person.added_on && String(person.added_on).trim().length > 0
            ? person.added_on
            : nowIso;
        }
        if (!person.added_on || String(person.added_on).trim().length === 0) {
          person.added_on = person.updated_at;
        }
      }

      // If we have a stable authenticated identity, normalize surveyor_id before pushing
      if (surveyorUuid) {
        for (const person of personsToUpload) {
          person.surveyor_id = surveyorUuid;
        }
      }

      const response = await this.apiClient.post(API_CONFIG.ENDPOINTS.SYNC_UPLOAD, {
        persons: personsToUpload,
        deleted_ids: deletedIdsToUpload,
        app_version: userData.app_version,
        updated_at: userData.updated_at,
      });

      const conflicts = Array.isArray(response.data?.conflicts) ? response.data.conflicts.length : 0;

      const acceptedPersonIds: string[] = Array.isArray(response.data?.accepted_person_ids)
        ? response.data.accepted_person_ids
        : [];
      const acceptedDeletedIds: string[] = Array.isArray(response.data?.accepted_deleted_ids)
        ? response.data.accepted_deleted_ids
        : [];

      // Mark as synced
      // IMPORTANT: Set synced_at to match the updated_at that was uploaded, NOT the current time
      // This prevents synced_at from being newer than updated_at (which would break isDirty check)
      if (acceptedPersonIds.length > 0) {
        const acceptedSet = new Set(acceptedPersonIds);
        for (const person of personsToUpload) {
          if (acceptedSet.has(person.id)) {
            // Use the person's updated_at timestamp, not "now"
            person.synced_at = person.updated_at || new Date().toISOString();
          }
        }
      }

      // Clear tombstones that the server accepted (idempotent)
      if (acceptedDeletedIds.length > 0 && Array.isArray((userData as any).deleted_persons)) {
        const acceptedDeletedSet = new Set(acceptedDeletedIds);
        (userData as any).deleted_persons = (userData as any).deleted_persons.filter((d: any) => !acceptedDeletedSet.has(d?.id));
      }

      // Update local storage
      await storageService.setUserData(userData);

      return { uploaded: acceptedPersonIds.length, conflicts };
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  }

  /**
   * Download data from server
   */
  private async downloadData(): Promise<{
    data: PersonRecord[];
    deletedIds: string[];
    downloaded: number;
    conflicts: number;
    serverTimestamp?: string;
  }> {
    try {
      const lastSyncTime = await storageService.getLastSyncTime();
      const since = lastSyncTime && lastSyncTime.trim().length > 0
        ? lastSyncTime
        : '1970-01-01T00:00:00.000Z';

      const response = await this.apiClient.get(API_CONFIG.ENDPOINTS.SYNC_DOWNLOAD, {
        params: {
          since,
        },
      });

      const serverPersons: PersonRecord[] = response.data.persons || [];
      const deletedIds: string[] = Array.isArray(response.data?.deleted_ids)
        ? response.data.deleted_ids
        : [];
      const serverTimestamp: string | undefined = typeof response.data?.server_timestamp === 'string'
        ? response.data.server_timestamp
        : undefined;

      return {
        data: serverPersons,
        deletedIds,
        downloaded: serverPersons.length,
        conflicts: 0, // Will be calculated during merge
        serverTimestamp,
      };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  /**
   * Merge server data with local data
   * Handles conflicts by taking the most recent update
   */
  private async mergeAndSave(
    localData: UserAppData,
    serverPersons: PersonRecord[],
    deletedIds: string[] = []
  ): Promise<void> {
    await logService.info('Sync Merge', 'Starting merge', {
      localPersonsCount: localData.persons?.length || 0,
      serverPersonsCount: serverPersons.length,
      deletedIdsCount: deletedIds.length,
    });

    const localPersons = Array.isArray((localData as any).persons) ? (localData as any).persons : [];
    const mergedPersons = [...localPersons];

    const localDeletedPersons: Array<{ id: string; deleted_at: string }> = Array.isArray((localData as any).deleted_persons)
      ? (localData as any).deleted_persons
      : [];
    const localDeletedSet = new Set(localDeletedPersons.map((d) => d.id));

    let newFromServer = 0;
    let updatedFromServer = 0;
    let skippedTombstoned = 0;
    let keptLocal = 0;

    for (const serverPerson of serverPersons) {
      // If we have a local tombstone, never resurrect from server.
      if (localDeletedSet.has(serverPerson.id)) {
        await logService.info('Sync Merge', `Skipping server person ${serverPerson.id} (${serverPerson.name}) - has local tombstone`);
        skippedTombstoned++;
        continue;
      }

      const localIndex = mergedPersons.findIndex((p) => p.id === serverPerson.id);

      if (localIndex === -1) {
        // New person from server - mark as synced
        serverPerson.synced_at = serverPerson.updated_at;
        await logService.info('Sync Merge', `Adding new person from server: ${serverPerson.id} (${serverPerson.name}), records: ${serverPerson.records?.length || 0}`);
        mergedPersons.push(serverPerson);
        newFromServer++;
      } else {
        // Existing person - resolve conflict
        const localPerson = mergedPersons[localIndex];
        const localUpdated = new Date(localPerson.updated_at || 0);
        const serverUpdated = new Date(serverPerson.updated_at || 0);

        if (serverUpdated > localUpdated) {
          // Server is newer, use server version and mark as synced
          serverPerson.synced_at = serverPerson.updated_at;
          await logService.info('Sync Merge', `Updating person ${serverPerson.id} (${serverPerson.name}) from server (server newer: ${serverUpdated.toISOString()} > ${localUpdated.toISOString()})`);
          mergedPersons[localIndex] = serverPerson;
          updatedFromServer++;
        } else {
          // Local is newer or equal, keep local version (should already be uploaded and have synced_at set)
          await logService.info('Sync Merge', `Keeping local person ${localPerson.id} (${localPerson.name}) (local newer or equal: ${localUpdated.toISOString()} >= ${serverUpdated.toISOString()})`);
          keptLocal++;
        }
        // else: Local is newer or equal, keep local version (already uploaded)
      }
    }

    await logService.info('Sync Merge', 'Merge stats', {
      newFromServer,
      updatedFromServer,
      keptLocal,
      skippedTombstoned,
    });

    // Apply server-side deletions
    if (deletedIds.length > 0) {
      const deletedSet = new Set(deletedIds);
      for (const deletedId of deletedIds) {
        const idx = mergedPersons.findIndex((p) => p.id === deletedId);
        if (idx !== -1) {
          await logService.info('Sync Merge', `Removing person ${deletedId} (deleted on server)`);
          mergedPersons.splice(idx, 1);
        }
      }

      // Clear local tombstones for deletions that are now confirmed by server
      if (Array.isArray((localData as any).deleted_persons)) {
        (localData as any).deleted_persons = (localData as any).deleted_persons.filter((d: any) => !deletedSet.has(d?.id));
      }
    }

    // Update local data
    const updatedData: UserAppData = {
      ...localData,
      persons: mergedPersons,
      updated_at: new Date().toISOString(),
      synced_at: new Date().toISOString(),
      pending_sync: false,
    };

    // Save to storage - this MUST succeed or we throw
    await storageService.setUserData(updatedData);
    
    // Verify the save was successful by reading it back
    const verifyData = await storageService.getUserData();
    if (!verifyData || verifyData.persons?.length !== mergedPersons.length) {
      throw new Error('Failed to verify saved data - sync aborted to prevent data loss');
    }
    
    await logService.info('Sync Merge', `Merge complete, saved to storage with ${mergedPersons.length} persons`);
    
    // Notify that data has been updated (triggers UserContext to reload)
    window.dispatchEvent(new CustomEvent('userDataUpdated'));
  }

  /**
   * Get sync status
   */
  async getSyncStatus(): Promise<SyncStatus> {
    const userData = await storageService.getUserData();
    const lastSyncTime = await storageService.getLastSyncTime();

    const persons: PersonRecord[] = Array.isArray(userData?.persons) ? userData.persons : [];
    const deletedPersons: Array<{ id: string; deleted_at: string }> = Array.isArray((userData as any)?.deleted_persons)
      ? (userData as any).deleted_persons
      : [];

    // Get current user's UUID to filter out offline user data
    const currentUser = await authService.getCurrentUser();
    const userAny = currentUser as any;
    const surveyorUuid: string | undefined = (typeof userAny?.uuid === 'string' && userAny.uuid.length > 0)
      ? userAny.uuid
      : (currentUser?.id ? String(currentUser.id) : undefined);

    // Only count pending changes for the current user (exclude offline user data)
    const pendingPersons = persons.filter((person) => {
      // Skip if not current user's data
      if (surveyorUuid && person.surveyor_id !== surveyorUuid) return false;
      
      if (!person.synced_at) return true;
      const updated = new Date(person.updated_at || 0).getTime();
      const synced = new Date(person.synced_at || 0).getTime();
      return updated > synced;
    }).length;

    // Filter deleted persons to only include current user's
    const pendingDeleted = deletedPersons.filter((dp: any) => {
      return !surveyorUuid || dp.surveyor_id === surveyorUuid;
    }).length;

    const pendingChanges = pendingPersons + pendingDeleted;

    return {
      isSyncing: this.isSyncing,
      lastSyncAt: lastSyncTime || undefined,
      lastSyncResult: this.lastSyncResult,
      pendingChanges,
      progress: this.isSyncing ? this.progress : undefined,
      stage: this.isSyncing ? this.stage : (this.lastSyncResult ? this.stage : 'idle'),
    };
  }

  /**
   * Check if device is on WiFi
   * Note: This is a placeholder. In Capacitor, use Network plugin
   */
  private isOnWiFi(): boolean {
    // TODO: Implement actual WiFi check using Capacitor Network plugin
    // For now, assume always on WiFi in browser
    if (typeof navigator !== 'undefined' && 'connection' in navigator) {
      const connection = (navigator as any).connection;
      return connection.type === 'wifi';
    }
    return true; // Default to true in browser
  }

  /**
   * Subscribe to sync status changes
   */
  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.syncListeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.syncListeners.indexOf(listener);
      if (index > -1) {
        this.syncListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of sync status change
   */
  private async notifyListeners(): Promise<void> {
    const status = await this.getSyncStatus();
    this.syncListeners.forEach((listener) => listener(status));
  }

  /**
   * Force a full sync (re-download all data)
   */
  async fullSync(): Promise<SyncResult> {
    // Clear last sync time to force full download
    await storageService.setLastSyncTime('');
    return await this.syncNow();
  }

  /**
   * Get all deleted persons for current user from server
   */
  async getDeletedPersons(): Promise<Array<{ id: string; name: string; deleted_at: string; surveyor_id: string }>> {
    try {
      const response = await this.apiClient.get('/api/v1/sync/deleted-persons');
      return response.data.deleted_persons || [];
    } catch (error: any) {
      logService.error('Failed to get deleted persons from server', error);
      throw error;
    }
  }

  /**
   * Clean up on app close
   */
  destroy(): void {
    this.stopPeriodicSync();
    this.syncListeners = [];
  }
}

// Export singleton instance
export const syncService = new SyncService();

// Export class for testing
export default SyncService;
